import {FELT} from './felt';

/**
 * Small felt drawings of the course's friends for the map toys: Beanie,
 * Ollie, the orange tabby and Plush π, each about 18 viewBox units across
 * and centered at the origin so a toy can place them with one transform.
 * They are SVG groups with no text; the toy that draws them names them.
 */

/** A darker cardinal for knit ribs and shadows. */
const CARDINAL_DARK = '#7A1712';

/** How a small friend looks. */
export type Mood = 'calm' | 'happy' | 'surprised' | 'asleep';

/** Ollie's colours, from `../mascot/OlliePlush.tsx`. */
export const OLLIE = {
  fur: '#A87A52',
  furLight: '#C9A077',
  dark: '#6E4A2F',
  muzzle: '#EBD6BC',
  belly: '#D2B08A',
  nose: '#3A2418',
} as const;

/**
 * Eyes, blush and mouth for a small face centered at the origin.
 *
 * @param props.mood - Which eyes and mouth to draw.
 * @param props.eyeGap - Half the distance between the eyes. Omitted means
 *     1.7 units.
 * @return The face.
 * @throws Never.
 */
export function Face({mood, eyeGap = 1.7}: {mood: Mood; eyeGap?: number}) {
  return (
    <g>
      {mood === 'asleep' ? (
        [-eyeGap, eyeGap].map(cx => (
          <path
            key={cx}
            d={`M ${cx - 0.8} 1.4 Q ${cx} 2.2 ${cx + 0.8} 1.4`}
            stroke={FELT.ink}
            strokeWidth={0.5}
            fill="none"
            strokeLinecap="round"
          />
        ))
      ) : (
        <>
          <circle cx={-eyeGap} cy={1.5} r={0.65} fill={FELT.ink} />
          <circle cx={eyeGap} cy={1.5} r={0.65} fill={FELT.ink} />
        </>
      )}
      <circle
        cx={-eyeGap - 1.4}
        cy={2.9}
        r={0.9}
        fill={FELT.pink}
        opacity={0.6}
      />
      <circle
        cx={eyeGap + 1.4}
        cy={2.9}
        r={0.9}
        fill={FELT.pink}
        opacity={0.6}
      />
      {mood === 'happy' && (
        <path
          d="M -1 3.2 Q 0 4.3 1 3.2"
          stroke={FELT.ink}
          strokeWidth={0.5}
          fill="none"
          strokeLinecap="round"
        />
      )}
      {mood === 'surprised' && (
        <circle cx={0} cy={3.7} r={0.6} fill={FELT.ink} />
      )}
    </g>
  );
}

/**
 * Beanie, the cream bean in a cardinal knit beanie, about 13 units tall.
 *
 * @param props.mood - Beanie's expression.
 * @return The drawing.
 * @throws Never.
 */
export function Beanie({mood}: {mood: Mood}) {
  return (
    <g>
      <ellipse
        cx={0}
        cy={1}
        rx={4.6}
        ry={5.2}
        fill={FELT.cream}
        stroke={FELT.creamDark}
        strokeWidth={0.6}
      />
      <path
        d="M -4.4 -0.8 Q -4.4 -6.6 0 -6.8 Q 4.4 -6.6 4.4 -0.8 Z"
        fill={FELT.cardinal}
      />
      <rect
        x={-4.8}
        y={-1.6}
        width={9.6}
        height={1.8}
        rx={0.9}
        fill={CARDINAL_DARK}
      />
      <circle cx={0} cy={-7.3} r={1.5} fill={FELT.cardinal} />
      <Face mood={mood} />
    </g>
  );
}

/**
 * Ollie the otter's head, without the lucky die: small round ears, a wide
 * cream muzzle, a button nose and whisker dots.
 *
 * @param props.mood - Ollie's expression.
 * @param props.fur - The fur colour. Omitted means Ollie's own brown.
 * @return The drawing.
 * @throws Never.
 */
export function OllieHead({mood, fur = OLLIE.fur}: {mood: Mood; fur?: string}) {
  return (
    <g>
      {[-6.4, 6.4].map(cx => (
        <g key={cx}>
          <circle cx={cx} cy={-6} r={2} fill={fur} />
          <circle cx={cx} cy={-5.9} r={0.9} fill={OLLIE.dark} />
        </g>
      ))}
      <ellipse cx={0} cy={0} rx={8.6} ry={8.2} fill={fur} />
      <ellipse cx={0} cy={2.8} rx={6.4} ry={3.9} fill={OLLIE.muzzle} />
      <g transform="translate(0 -2.4) scale(1.2)">
        <Face mood={mood} eyeGap={2.4} />
      </g>
      <path
        d="M -1.3 1.5 Q 0 1.2 1.3 1.5 Q 0.9 2.8 0 2.8 Q -0.9 2.8 -1.3 1.5 Z"
        fill={OLLIE.nose}
      />
      {[
        {cx: -3.8, cy: 3.4},
        {cx: -4.4, cy: 4.5},
        {cx: 3.8, cy: 3.4},
        {cx: 4.4, cy: 4.5},
      ].map(({cx, cy}) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={0.4} fill={OLLIE.dark} />
      ))}
    </g>
  );
}

/** Where each pip sits on a die face, as offsets from the face's center. */
const PIPS: Record<number, readonly (readonly [number, number])[]> = {
  1: [[0, 0]],
  2: [
    [-1, -1],
    [1, 1],
  ],
  3: [
    [-1, -1],
    [0, 0],
    [1, 1],
  ],
  4: [
    [-1, -1],
    [1, -1],
    [-1, 1],
    [1, 1],
  ],
  5: [
    [-1, -1],
    [1, -1],
    [0, 0],
    [-1, 1],
    [1, 1],
  ],
  6: [
    [-1, -1],
    [1, -1],
    [-1, 0],
    [1, 0],
    [-1, 1],
    [1, 1],
  ],
};

/**
 * Ollie's cardinal lucky die, 6 units square, centered at the origin.
 *
 * @param props.face - The face showing, 1 to 6. Omitted means three, the
 *     diagonal Ollie's plush holds.
 * @return The drawing.
 * @throws Never.
 */
export function LuckyDie({face = 3}: {face?: number}) {
  return (
    <g>
      <rect x={-3} y={-3} width={6} height={6} rx={1.4} fill={FELT.cardinal} />
      {(PIPS[face] ?? PIPS[3] ?? []).map(([dx, dy]) => (
        <circle
          key={`${dx}-${dy}`}
          cx={dx * 1.4}
          cy={dy * 1.4}
          r={0.6}
          fill="#FFF6E8"
        />
      ))}
    </g>
  );
}

/**
 * Ollie's face over their cardinal lucky die, about 18 units across;
 * golden when legendary.
 *
 * @param props.mood - Ollie's expression.
 * @param props.isGolden - Draws the legendary golden Ollie. Omitted means
 *     Ollie's own brown.
 * @return The drawing.
 * @throws Never.
 */
export function OllieFace({
  mood,
  isGolden = false,
}: {
  mood: Mood;
  isGolden?: boolean;
}) {
  return (
    <g>
      <OllieHead mood={mood} fur={isGolden ? FELT.gold : OLLIE.fur} />
      <g transform="translate(0 8.6) rotate(-8)">
        <LuckyDie />
      </g>
    </g>
  );
}

/**
 * An orange tabby's face, about 18 units across: the toolbox cat.
 *
 * @param props.mood - The cat's expression.
 * @return The drawing.
 * @throws Never.
 */
export function CatFace({mood}: {mood: Mood}) {
  return (
    <g>
      <path d="M -8 -2 L -7 -10 L -2 -7 Z" fill="#E8954A" />
      <path d="M 8 -2 L 7 -10 L 2 -7 Z" fill="#E8954A" />
      <path d="M -6.6 -3.4 L -6.4 -7.6 L -4 -6 Z" fill={FELT.pink} />
      <path d="M 6.6 -3.4 L 6.4 -7.6 L 4 -6 Z" fill={FELT.pink} />
      <ellipse cx={0} cy={0} rx={8.6} ry={7.8} fill="#E8954A" />
      {[-2.4, 0, 2.4].map(x => (
        <path
          key={x}
          d={`M ${x} -7.4 V -4.6`}
          stroke="#B8642A"
          strokeWidth={1}
          strokeLinecap="round"
        />
      ))}
      <g transform="translate(0 -1) scale(1.3)">
        <Face mood={mood} eyeGap={2.4} />
      </g>
      <path d="M -0.9 2 H 0.9 L 0 3 Z" fill={FELT.pink} />
      {[-1, 1].map(side => (
        <path
          key={side}
          d={`M ${side * 3} 3 L ${side * 8} 2 M ${side * 3} 3.8 L ${side * 8} 4.4`}
          stroke={FELT.creamLight}
          strokeWidth={0.4}
        />
      ))}
    </g>
  );
}

/**
 * Plush π, a stitched felt π about 16 units across.
 *
 * @param props.mood - Plush π's expression.
 * @return The drawing.
 * @throws Never.
 */
export function PlushPiMark({mood}: {mood: Mood}) {
  return (
    <g>
      <rect
        x={-8}
        y={-6}
        width={16}
        height={4.6}
        rx={2.3}
        fill={FELT.cardinal}
      />
      <path
        d="M -4.6 -2 Q -5 4 -6.6 7.4"
        stroke={FELT.cardinal}
        strokeWidth={3.2}
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M 3.6 -2 Q 3.6 5 6 7"
        stroke={FELT.cardinal}
        strokeWidth={3.2}
        fill="none"
        strokeLinecap="round"
      />
      <g transform="translate(0 -5.6) scale(0.8)">
        <Face mood={mood} eyeGap={2} />
      </g>
    </g>
  );
}

/**
 * A four-point gold sparkle.
 *
 * @param props.x - Center, horizontally.
 * @param props.y - Center, vertically.
 * @param props.r - Distance from the center to each point.
 * @return The drawing.
 * @throws Never.
 */
export function Sparkle({x, y, r}: {x: number; y: number; r: number}) {
  const k = r * 0.3;
  return (
    <path
      d={`M ${x} ${y - r} L ${x + k} ${y - k} L ${x + r} ${y} L ${x + k} ${y + k} L ${x} ${y + r} L ${x - k} ${y + k} L ${x - r} ${y} L ${x - k} ${y - k} Z`}
      fill={FELT.gold}
    />
  );
}
